/**
 * Настройки участка (sector_options). Бэкенд хранит каждую настройку отдельной строкой
 * { sector, group, name, number_value | char_value }: числовые и булевы значения — в number_value,
 * текстовые (стратегия распределения) — в char_value.
 */
export interface SectorOption {
  id: number;
  sector: number;
  group: number;
  name: string;
  number_value?: number | null;
  char_value?: string | null;
}

export type SectorOptionPayload = Omit<SectorOption, 'id'>;

export type SectorOptionValueField = 'number_value' | 'char_value';

export interface SectorOptionValue {
  field: SectorOptionValueField;
  value: number | string;
}

/** Группа настроек автоматического распределения — один набор на участок */
export const AUTO_DISTRIBUTION_OPTION_GROUP = 1;

export enum AutoDistributionOptionName {
  Autoassigned = 'autoassigned',
  AssignAfterResult = 'assign_after_result',
  LimitationType = 'limitation_type',
  DistributionStrategy = 'distribution_strategy',
  AllowedOrderOverlapMinutes = 'allowed_order_overlap_minutes',
}

export type DistributionStrategy = 'minimize_total_travel_time' | 'balanced_distribution' | 'priority_based';
export type LimitationType = 1 | 2 | 3;

export const DISTRIBUTION_STRATEGIES: DistributionStrategy[] = [
  'minimize_total_travel_time',
  'balanced_distribution',
  'priority_based',
];

/** Допустимое пересечение заявок по времени, минуты */
export const ORDER_OVERLAP_MINUTES_MIN = 0;
export const ORDER_OVERLAP_MINUTES_MAX = 600;

export interface AutoDistributionOptions {
  autoassigned: boolean;
  assignAfterResult: boolean;
  limitationType: LimitationType;
  distributionStrategy: DistributionStrategy;
  allowedOrderOverlapMinutes: number;
}

export const DEFAULT_AUTO_DISTRIBUTION_OPTIONS: AutoDistributionOptions = {
  autoassigned: true,
  // По умолчанию — «Распределить и Сохранить»
  assignAfterResult: true,
  // По умолчанию — жёсткая стратегия распределения по районам
  limitationType: 3,
  distributionStrategy: 'minimize_total_travel_time',
  allowedOrderOverlapMinutes: 0,
};

export const AUTO_DISTRIBUTION_OPTION_NAMES: AutoDistributionOptionName[] = [
  AutoDistributionOptionName.Autoassigned,
  AutoDistributionOptionName.AssignAfterResult,
  AutoDistributionOptionName.LimitationType,
  AutoDistributionOptionName.DistributionStrategy,
  AutoDistributionOptionName.AllowedOrderOverlapMinutes,
];

export function normalizeOrderOverlapMinutes(value: unknown): number {
  const minutes = Math.round(Number(value));

  if (!Number.isFinite(minutes)) {
    return DEFAULT_AUTO_DISTRIBUTION_OPTIONS.allowedOrderOverlapMinutes;
  }

  return Math.min(Math.max(minutes, ORDER_OVERLAP_MINUTES_MIN), ORDER_OVERLAP_MINUTES_MAX);
}

/** Значение настройки для отправки на бэкенд и поле, в которое оно кладётся */
export function autoDistributionOptionValue(
  options: AutoDistributionOptions,
  name: AutoDistributionOptionName
): SectorOptionValue {
  switch (name) {
    case AutoDistributionOptionName.Autoassigned:
      return { field: 'number_value', value: options.autoassigned ? 1 : 0 };
    case AutoDistributionOptionName.AssignAfterResult:
      return { field: 'number_value', value: options.assignAfterResult ? 1 : 0 };
    case AutoDistributionOptionName.LimitationType:
      return { field: 'number_value', value: options.limitationType };
    case AutoDistributionOptionName.DistributionStrategy:
      return { field: 'char_value', value: options.distributionStrategy };
    case AutoDistributionOptionName.AllowedOrderOverlapMinutes:
      return { field: 'number_value', value: normalizeOrderOverlapMinutes(options.allowedOrderOverlapMinutes) };
  }
}

/** Совпадает ли сохранённая запись с новым значением (чтобы не слать лишний PATCH) */
export function isSectorOptionValueEqual(record: SectorOption, optionValue: SectorOptionValue): boolean {
  if (optionValue.field === 'char_value') {
    const charValue = record.char_value === null || record.char_value === undefined ? '' : record.char_value;

    return String(charValue) === String(optionValue.value);
  }

  const numberValue = record.number_value;

  return numberValue !== null && numberValue !== undefined && Number(numberValue) === Number(optionValue.value);
}

/** Собирает настройки из записей участка; отсутствующие и некорректные значения берутся по умолчанию */
export function autoDistributionOptionsFromRecords(records: SectorOption[]): AutoDistributionOptions {
  const options: AutoDistributionOptions = Object.assign({}, DEFAULT_AUTO_DISTRIBUTION_OPTIONS);
  const recordOf = (name: AutoDistributionOptionName): SectorOption | undefined =>
    (records || []).find((item) => item && item.name === name);
  const numberOf = (name: AutoDistributionOptionName): number | null => {
    const record = recordOf(name);
    const raw = record && record.number_value !== null && record.number_value !== undefined ? record.number_value : null;
    const value = raw === null ? NaN : Number(raw);

    return Number.isFinite(value) ? value : null;
  };

  const autoassigned = numberOf(AutoDistributionOptionName.Autoassigned);
  if (autoassigned !== null) {
    options.autoassigned = autoassigned === 1;
  }

  const assignAfterResult = numberOf(AutoDistributionOptionName.AssignAfterResult);
  if (assignAfterResult !== null) {
    options.assignAfterResult = assignAfterResult === 1;
  }

  const limitationType = numberOf(AutoDistributionOptionName.LimitationType);
  if ([1, 2, 3].indexOf(limitationType) > -1) {
    options.limitationType = limitationType as LimitationType;
  }

  const strategyRecord = recordOf(AutoDistributionOptionName.DistributionStrategy);
  const strategy = strategyRecord && strategyRecord.char_value ? String(strategyRecord.char_value).trim() : null;
  if (strategy && DISTRIBUTION_STRATEGIES.indexOf(strategy as DistributionStrategy) > -1) {
    options.distributionStrategy = strategy as DistributionStrategy;
  }

  const overlapMinutes = numberOf(AutoDistributionOptionName.AllowedOrderOverlapMinutes);
  if (overlapMinutes !== null) {
    options.allowedOrderOverlapMinutes = normalizeOrderOverlapMinutes(overlapMinutes);
  }

  return options;
}
